/**
 * The store's boot-time reads of browser state — the three things taskStore.ts
 * does at module evaluation before `create()` runs: rehydrate tasks from
 * sessionStorage, recover the selected task from the URL, and sweep stale
 * persisted entries once.
 *
 * Its own module (file-size ratchet — taskStore.ts only shrinks) and a leaf:
 * it imports persistence and types, never taskStore, so the dependency keeps
 * running store → module and no cycle closes. Each read stays a FUNCTION rather
 * than a module-level constant so taskStore calls them at exactly the point the
 * inline code ran — the cleanup must still happen after the sessionStorage read,
 * not at import time.
 */

import { getStoredTasks, cleanupStaleEntries } from "./persistence";
import type { TaskState } from "./types";

/** Tasks rehydrated from sessionStorage, or an empty map on the server. */
export function readInitialTasks(): Map<string, TaskState> {
  return (
    (typeof window !== "undefined" ? getStoredTasks() : null) ||
    new Map<string, TaskState>()
  );
}

/** selectedTaskId from the URL — the source of truth for which task is open. */
export function readInitialSelectedTaskId(): string | null {
  if (typeof window !== "undefined") {
    const match = window.location.pathname.match(/^\/task\/(.+)$/);
    if (match) return match[1];
  }
  return null;
}

/** Sweep stale persisted entries once, on the client only. */
export function runStaleCleanup(): void {
  if (typeof window !== "undefined") {
    cleanupStaleEntries();
  }
}
